import * as lodash from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { ApiSetFavoriteGet, ApiSetFavoriteUpdate } from '../../../api/front/favorite-set/api-favorite-set';
import { ResponseBase } from '../../../api/response-base';
import { useAppSelector } from '../../../app/hooks';
import { SetFavoriteItemResponse } from '../../../models/api/front/favorite-set';
import { apiActions } from '../../../slices/api-slice';
import { FavoriteBrand, favoriteBrandActions } from '../../../slices/favorite-brand-slice';
import { Button } from '../../ui/button/button';
import { dialogActions } from '../slice/dialog-slice';
import './favorite-selector-config.scss';
import { Input } from '../../ui/input/input';
import { LoadingPopup } from '../../ui/loading/loading-popup';
import { FavoriteSelectorConfig } from './favorite-selector-config';

type BrandProps = {
  brand?: FavoriteBrand,
  setSelectedID?: (id: string) => void,
  selected?: boolean,
  isChecked?: (id: string) => boolean,
  onRowCheck?: (id: string, checked: boolean) => void,
};

// - 商品の表示 -
const Brand = (props: BrandProps) => {
  const {
    brand,
    setSelectedID,
    selected,
    isChecked,
    onRowCheck,
  } = props;

  // -- 商品押下 --
  const handlerClickBrand = useCallback((selectId?: string) => {
    if (selectId === undefined) return;
    setSelectedID?.(selectId);
    const nextChecked = !Boolean(isChecked?.(selectId));
    onRowCheck?.(selectId, nextChecked);
  }, [
    setSelectedID,
    isChecked,
    onRowCheck,
  ]);

  return (
    <>
      <div
        className={`favorite_brand_config__table__body__row${selected ? ' selected' : ''}`}
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          handlerClickBrand(brand?.id);
        }}
      >
        {brand ? (
          <div className="favorite_brand_config__table__body__row__checkbox">
            <input
              type="checkbox"
              checked={Boolean(isChecked?.(brand.id))}
              onChange={(e) => onRowCheck?.(brand.id, e.target.checked)}
              onClick={(e) => e.stopPropagation()}
              aria-label="商品を選択"
            />
          </div>
        ) : (
          <div className="favorite_brand_config__table__body__row__checkbox placeholder" />
        )}
        <div className="favorite_brand_config__table__body__row__data">{brand?.name.replaceAll(' ', '\u00A0')}</div>
      </div>
    </>
  );
};

/**
 * お気に入り商品配列が「同一内容か」を判定する
 * - 比較対象は id のみ（商品名や順序は考慮しない）
 * - 並び順が異なっていても同じ ID 集合であれば true
 * - 件数が異なる場合は不一致とする
 */
const isSameFavProducts = (
  a: { id: string }[],
  b: { id: string }[],
): boolean => {
  if (a.length !== b.length) return false;
  const aIds = a.map(v => v.id).sort();
  const bIds = b.map(v => v.id).sort();
  return aIds.every((id, index) => id === bIds[index]);
};

type FavoriteSetEditDialogProps = {
  kijshopCd: string,
  setId: string;
  closeButtonLabel?: string,
  // - 閉じるボタンcallback -
  closeButtonCallback?: () => void,
  // - OKボタンcallback -
  okButtonCallback?: () => void,
};

export const FavoriteSetEditDialog = (props: FavoriteSetEditDialogProps) => {
  const {
    kijshopCd,
    setId,
    closeButtonLabel,
    closeButtonCallback,
    okButtonCallback,
  } = props;
  // - Hooks -
  const dispatch = useDispatch();
  const { activeBrands } = useAppSelector((state) => ({ activeBrands: state.favoriteBrand.activeFavoriteBrands }), lodash.isEqual);

  // - State -
  const [inputName, setInputName] = useState('');
  const [initialFavBrands, setInitialFavBrands] = useState<FavoriteBrand[]>([]);
  const [selectedId, setSelectedId] = useState('');
  const [checkedIds, setCheckedIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [loadingInit, setLoadingInit] = useState(true);

  // - Callback -
  // -- 商品チェック --
  const handleBrandCheck = useCallback((id: string, checked: boolean) => {
    setCheckedIds(prev => {
      const next = new Set(prev);
      if (checked) next.add(id); else next.delete(id);
      return next;
    });
  }, []);

  const isChecked = useCallback((id: string) => {
    return checkedIds.has(String(id));
  }, [checkedIds]);

  // 編集時の処理
  const handlerClickSetUpdate = useCallback(() => {
    // セット登録名の取得
    const name = inputName.trim();
    if (!name) {
      dispatch(dialogActions.pushMessage({
        title: '確認',
        message: [
          'セット名を入力してください'
        ],
        buttons: [
          {
            label: 'OK',
            callback: () => dispatch(dialogActions.pop()),
          },
        ],
      }));
      return;
    }
    // 選択されたお気に入り商品のidを取得して、配列にまとめる
    const favProductIds = activeBrands.map(brand => brand.id);
    // APIの編集処理
    setLoading(true);
    dispatch(apiActions.run(
      new ApiSetFavoriteUpdate(
        setId,
        { kijshopCd, name, favProductIds }
      ),
      {
        onSuccess: () => {
          setLoading(false);
          dispatch(favoriteBrandActions.setSelectBrand(undefined));
          dispatch(favoriteBrandActions.setActiveFavoriteBrands([]));
          okButtonCallback?.();
        },
        onBasicError: () => {
          setLoading(false);
          dispatch(dialogActions.pushMessage({
            title: '確認',
            message: [
              '入力エラーです。内容を確認してください。'
            ],
            buttons: [
              {
                label: 'OK',
                callback: () => dispatch(dialogActions.pop()),
              },
            ],
          }));
        },
        onError: () => {
          setLoading(false);
          dispatch(dialogActions.pushMessage({
            title: '確認',
            message: [
              '登録に失敗しました。時間をおいて再度お試しください。'
            ],
            buttons: [
              {
                label: 'OK',
                callback: () => dispatch(dialogActions.pop()),
              },
            ],
          }));
        },
      },
      { apiName: `favoriteSet:update:${setId}` }
    ));
  }, [
    inputName,
    activeBrands,
  ]);

  // -- 閉じるボタン押下 --
  const handlerClickClose = useCallback(() => {
    dispatch(dialogActions.pop());
    dispatch(favoriteBrandActions.setSelectBrand(undefined));
    dispatch(favoriteBrandActions.setActiveFavoriteBrands([]));
  }, []);

  // -- 削除ボタン押下 --
  const handlerClickDeleteButton = useCallback(() => {
    dispatch(dialogActions.pushMessage({
      title: '確認',
      message: [
        '選択した商品をセットから除外します。',
        '確定ボタンを押すまで削除は確定しません。',
      ],
      buttons: [
        {
          label: 'キャンセル',
          callback: () => dispatch(dialogActions.pop()),
        },
        {
          label: '確認',
          callback: () => {
            dispatch(favoriteBrandActions.setActiveFavoriteBrands(activeBrands.filter(v => !checkedIds.has(v.id))));
            dispatch(dialogActions.pop());
          }
        },
      ]
    }))
  }, [
    checkedIds,
    activeBrands,
  ]);

  // -- 商品追加ボタン押下 --
  const handlerClickAddBrands = useCallback(() => {
    dispatch(dialogActions.push({
      title: 'お気に入りセット商品保存',
      element: (
        <FavoriteSelectorConfig
          kijshopCd={kijshopCd}
          mode="draft"
          setName={inputName}
        />
      ),
    }));
  }, [inputName]);

  // - Effect -
  useEffect(() => {
    dispatch(
      apiActions.run(
        new ApiSetFavoriteGet(setId, { kijshopCd }),
        {
          onSuccess: (res: ResponseBase<SetFavoriteItemResponse>) => {
            const d = res.body.data;
            // セット名を反映
            setInputName(d?.name ?? '');
            // お気に入り商品をセット
            const favBrands = d?.favProducts ?? [];
            setInitialFavBrands(favBrands);
            dispatch(favoriteBrandActions.setActiveFavoriteBrands(favBrands));
            setLoadingInit(false);
          },
          onError: () => {
            setLoadingInit(false);
            dispatch(dialogActions.pushMessage({
              title: '確認',
              message: [
                'セット情報の取得に失敗しました'
              ],
              buttons: [
                {
                  label: 'OK',
                  callback: () => dispatch(dialogActions.pop()),
                },
              ],
            }));
          },
        },
        { apiName: `favoriteSet:get:${setId}` } // 連打対策（最新のみ採用）
      )
    );
  }, []);

  // - Memo -
  const isConfirmDisabled = useMemo(() => {
    return isSameFavProducts(initialFavBrands, activeBrands);
  }, [
    initialFavBrands,
    activeBrands,
  ]);

  return (
    <div className="dialog_contents favorite_selector_config">
      <div className='favorite_selector_config__content'>登録することで、次回から簡単に呼び出せます。</div>
      <div className='favorite_selector_config__content'>お気に入りセット商品の名称を入力してください</div>
      <Input
        value={inputName}
        onChange={(e) => setInputName(e.target.value)}
        maxLength={40}
      />
      <div className='favorite_selector_config__content__warning'>※ 40文字以内で入力してください</div>
      <div className='favorite_selector_config__table_message'>セット商品</div>
      <div className="dialog_contents__body favorite_selector_config__table">
        <div className="favorite_selector_config__table__inner">
          <div className="favorite_selector_config__table__header">お気に入りセット商品保存</div>
          <div className="favorite_selector_config__table__body">
            {activeBrands.map((v, i) => (
              <Brand
                key={`favorite-set-edit-brand_${i}_${v.id}_${v.name}`}
                brand={v}
                setSelectedID={setSelectedId}
                selected={selectedId === v.id}
                isChecked={isChecked}
                onRowCheck={handleBrandCheck}
              />
            ))}
            {activeBrands.length < 10 ? ([...new Array(10 - activeBrands.length)].map((_, i) => (
              <Brand key={`favorite-set-edit_${i}_dummy`} />
            ))) : (<></>)}
          </div>
        </div>
      </div>
      <div className="dialog_contents__footer">
        <div className="dialog_contents__footer__left">
          <Button
            label={closeButtonLabel ? closeButtonLabel : 'キャンセル'}
            onClick={closeButtonCallback ? closeButtonCallback : handlerClickClose}
          />
        </div>
        <div className="dialog_contents__footer__center">
          <Button
            label="削除"
            color="danger"
            disabled={!activeBrands.some(brand => checkedIds.has(String(brand.id)))}
            onClick={handlerClickDeleteButton}
          />
          <Button
            label="商品追加"
            onClick={handlerClickAddBrands}
          />
        </div>
        <div className="dialog_contents__footer__right">
          <Button
            label="OK"
            onClick={handlerClickSetUpdate}
            disabled={isConfirmDisabled || activeBrands.length === 0}
          />
        </div>
      </div>
      {loadingInit ? <LoadingPopup key={`fav-loading`} /> : <></>}
      {!loadingInit && loading ? <LoadingPopup key={`fav-loading`} label={'更新中です'} /> : <></>}
    </div>
  );
};
